'use client';

import React, { useRef, useEffect, useState } from "react";
import RoundedIcon from "@/components/ui/RoundedIcon/RoundedIcon";
import { Check, Gift, SunDim, User } from "@phosphor-icons/react";
import Headline from "@/components/ui/Headline/Headline";
import TextInputField from "@/components/ui/TextInputField/TextInputField";
import TextAreaField from "@/components/ui/TextAreaField/TextAreaField";
import DropdownField from "@/components/ui/DropdownField/DropdownField";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { useDetectWrap } from "@/hooks/useDetectWrap";
import { SubmitFormButton } from "@/components/ui/SubmitFormButton";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import {
  createEmailChangeHandler,
  createPhoneChangeHandler,
  createRequiredFieldChangeHandler,
  createTextAreaChangeHandler,
  submitToZoho,
  checkAllMandatoryFields,
  checkFormValidationErrors,
  VALIDATION_HINTS,
  VALIDATION_HINTS_AR
} from "@/lib/formUtils";
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface ReferralSectionProps {
  language?: LanguageType
  heading: string
  subheading: string
  paragraph: string
  onInitialPosition?: (position: number) => void
  subheading2?: string
  paragraphList2: string[]
  subheading3?: string
  paragraphList3: string[]
  /** Names of staff to populate the "Recommended expert" dropdown.
   *  Provided by the parent page after filtering Strapi team members by
   *  position (e.g. "Beauty Therapist"). Empty array hides the dropdown
   *  options below the placeholder. */
  recommendedExperts?: string[]
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


const ReferralSection: React.FC<ReferralSectionProps> = ({
  language = "english",
  heading,
  subheading,
  paragraph,
  onInitialPosition,
  subheading2,
  paragraphList2,
  subheading3,
  paragraphList3,
  recommendedExperts = [],
}) => {
  const isLg = useIsLg();
  const { ref, isWrapped } = useDetectWrap();
  const sectionRef = useRef<HTMLElement>(null);
  const element1Ref = useRef<HTMLDivElement>(null);
  const element2Ref = useRef<HTMLDivElement>(null);
  const element3Ref = useRef<HTMLDivElement>(null);

  // Form state
  const [formData, setFormData] = useState({
    Referred_by_client_name: "", // Referrer's name
    Referrer_email: "",           // Referrer's email
    Referrer_phone: "",           // Referrer's phone
    Last_Name: "",                // Invitee's name
    Email: "",                    // Invitee's email
    Phone_number: "",             // Invitee's phone
    Recommended_Expert: "",       // Not submitted to Zoho
    Personal_Notes: ""            // Optional notes
  });

  // Validation states
  const [isReferrerNameInvalid, setIsReferrerNameInvalid] = useState(false);
  const [isReferrerEmailInvalid, setIsReferrerEmailInvalid] = useState(false);
  const [isReferrerPhoneInvalid, setIsReferrerPhoneInvalid] = useState(false);
  const [isInviteeNameInvalid, setIsInviteeNameInvalid] = useState(false);
  const [isInviteeEmailInvalid, setIsInviteeEmailInvalid] = useState(false);
  const [isInviteePhoneInvalid, setIsInviteePhoneInvalid] = useState(false);
  const [isNotesInvalid, setIsNotesInvalid] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Create handlers using utilities
  const handleReferrerNameChange = createRequiredFieldChangeHandler(setFormData, setIsReferrerNameInvalid, 'Referred_by_client_name');
  const handleReferrerEmailChange = createEmailChangeHandler(setFormData, setIsReferrerEmailInvalid, 'Referrer_email');
  const handleReferrerPhoneChange = createPhoneChangeHandler(setFormData, setIsReferrerPhoneInvalid, 'Referrer_phone');
  const handleInviteeNameChange = createRequiredFieldChangeHandler(setFormData, setIsInviteeNameInvalid, 'Last_Name');
  const handleInviteeEmailChange = createEmailChangeHandler(setFormData, setIsInviteeEmailInvalid, 'Email');
  const handleInviteePhoneChange = createPhoneChangeHandler(setFormData, setIsInviteePhoneInvalid, 'Phone_number');
  const handleNotesChange = createTextAreaChangeHandler(setFormData, setIsNotesInvalid, 'Personal_Notes', formData.Personal_Notes);

  // Check if all mandatory fields are filled
  const hasAllMandatoryFields = checkAllMandatoryFields(formData, [
    'Referred_by_client_name',
    'Referrer_email',
    'Referrer_phone',
    'Last_Name',
    'Email',
    'Phone_number'
  ]);

  // Check for duplicate email/phone between referrer and invitee
  const isDuplicateEmail = formData.Referrer_email.trim() !== '' &&
    formData.Email.trim() !== '' &&
    formData.Referrer_email.trim().toLowerCase() === formData.Email.trim().toLowerCase();

  const isDuplicatePhone = formData.Referrer_phone.trim() !== '' &&
    formData.Phone_number.trim() !== '' &&
    formData.Referrer_phone.trim() === formData.Phone_number.trim();

  // Check form validation errors
  const hasValidationErrors = checkFormValidationErrors(
    {
      referrerName: isReferrerNameInvalid,
      referrerEmail: isReferrerEmailInvalid || isDuplicateEmail,
      referrerPhone: isReferrerPhoneInvalid || isDuplicatePhone,
      inviteeName: isInviteeNameInvalid,
      inviteeEmail: isInviteeEmailInvalid || isDuplicateEmail,
      inviteePhone: isInviteePhoneInvalid || isDuplicatePhone,
      notes: isNotesInvalid
    },
    hasAllMandatoryFields
  );

  // Submit handler
  const handleSubmit = async () => {
    if (hasValidationErrors) return;

    setSubmitStatus('submitting');

    // Prepare data for Zoho (excluding Recommended_Expert)
    const zohoData = {
      Referred_by_client_name: formData.Referred_by_client_name,
      Referrer_email: formData.Referrer_email,
      Referrer_phone: formData.Referrer_phone,
      Last_Name: formData.Last_Name,
      Email: formData.Email,
      Phone_number: formData.Phone_number,
      Personal_Notes: formData.Personal_Notes,
      Recommended_Expert: formData.Recommended_Expert
    };

    const result = await submitToZoho(zohoData, 'Website - Referral');

    if (result.success) {
      setSubmitStatus('success');
      // Reset form after success
      // setTimeout(() => {
      //   setFormData({
      //     Referred_by_client_name: "",
      //     Referrer_email: "",
      //     Referrer_phone: "",
      //     Last_Name: "",
      //     Email: "",
      //     Phone_number: "",
      //     Recommended_Expert: "",
      //     Personal_Notes: ""
      //   });
      //   setSubmitStatus('idle');
      // }, 3000);
    } else {
      setSubmitStatus('error');
      // setTimeout(() => {
      //   setSubmitStatus('idle');
      // }, 3000);
    }
  };

  useEffect(() => {
    if (sectionRef.current && onInitialPosition) {
      const rect = sectionRef.current.getBoundingClientRect();
      const bottomY = window.scrollY + rect.bottom;
      onInitialPosition(bottomY);
    }
  }, [isLg, onInitialPosition]);

  // Reset submission status when form data changes
  useEffect(() => {
    if (submitStatus !== 'idle' && submitStatus !== 'submitting') {
      setSubmitStatus('idle');
    }
  }, [formData]);

  useGSAP(() => {
    const element1 = element1Ref.current;
    const element2 = element2Ref.current;
    const element3 = element3Ref.current;

    if (!element1 || !element3) return;

    const elements = [element1, element3];
    if (element2) {
      elements.push(element2);
    }

    // Set initial states for blur mask animation
    gsap.set(elements, {
      autoAlpha: 0,
      filter: 'blur(30px)',
      y: +100,
      force3D: true,
    });

    // Create scroll trigger animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: element1,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    });

    // Animate elements with stagger
    tl.to(elements, {
      autoAlpha: 1,
      filter: 'blur(0px)',
      y: 0,
      duration: 1.0,
      stagger: 0.2,
      ease: "power2.out",
      force3D: true,
    });

  }, { dependencies: [], revertOnUpdate: true });

  return (
    // section has custom padding!
    <section
      id="referral-section"
      ref={sectionRef}
      style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
      className="
      w-full
      px-[1.25rem]
      pt-[1rem]
      padding-main-x-desktop
      tb:pt-0
      flex items-center justify-center
    ">
      <div className="w-full container-medium">
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-y-[4.5rem] lg:gap-x-[4rem] lg:gap-y-[4.5rem]">
          <div className="w-full lg:w-[30.6875rem] lg:flex-grow lg:max-w-[49.6875rem] flex flex-col gap-[2rem] lg:gap-[4.5rem]">

            <div className="flex flex-row">
              <Headline
                type="short"
                heading={heading}
                subheading={subheading}
                paragraph={paragraph}
                language={language}
                alignment={language === "arabic" ? "right" : "left"}
              />
            </div>

            <div className="w-full tb:max-w-[30.6875rem] flex flex-col gap-[1.5rem] gap-[2rem]">
              <div ref={element1Ref} className="flex flex-col gap-[1.25rem] lg:gap-[1.5rem]">
                <div className="flex flex-row gap-[0.75rem] lg:gap-[1rem] items-center justify-start">
                  <RoundedIcon
                    className="rounded-full"
                    size={44}
                  >
                    <SunDim className="text-graphics-primary" />
                  </RoundedIcon>
                  <div className="flex flex-col gap-[0.5rem]">
                    <p className="text-text-primary text-28-to-20--neue-june">
                      {subheading2}
                    </p>
                  </div>
                </div>


                <div
                  className="flex flex-col gap-[0.75rem] items-start justify-start"
                  style={{
                    paddingInline: isLg ? "0.5rem" : "0 0.5rem"
                  }}
                >

                  {paragraphList2.map((paragraph, index) => (
                    <div key={index} className="flex flex-row gap-[0.75rem] items-start justify-start">
                      <div className="p-0 lg:p-[0.125rem]">
                        <Check className="text-graphics-secondary" size={20} />
                      </div>
                      <p className="text-16-to-14--alexandria text-text-tertiary">
                        {paragraph}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div ref={element2Ref} className="h-[1.5px] w-full bg-stroke-soft-200" />

              <div ref={element3Ref} className="flex flex-col gap-[1.5rem]">
                <div className="flex flex-row gap-[0.75rem] lg:gap-[1rem] items-center justify-start">
                  <RoundedIcon
                    className="rounded-full"
                    size={44}
                  >
                    <Gift className="text-graphics-primary" />
                  </RoundedIcon>
                  <div className="flex flex-col gap-[0.5rem]">
                    <p className="text-text-primary text-28-to-20--neue-june">
                      {subheading3}
                    </p>
                  </div>
                </div>


                <div
                  className="flex flex-col gap-[0.75rem] items-start justify-start"
                  style={{
                    paddingInline: isLg ? "0.5rem" : "0 0.5rem"
                  }}
                >

                  {paragraphList3.map((paragraph, index) => (
                    <div key={index} className="flex flex-row gap-[0.75rem] items-start justify-start">
                      <div className="p-0 lg:p-[0.125rem]">
                        <Check className="text-graphics-secondary" size={20} />
                      </div>
                      <p className="text-16-to-14--alexandria text-text-tertiary flex-wrap">
                        {paragraph}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>

          <div
            ref={ref}
            className="w-full flex flex-col lg:w-[30.6875rem] lg:flex-grow gap-y-[0.25rem] lg:gap-y-[0.75rem]"
            style={{
              maxWidth: isLg && !isWrapped ? '43.3125rem' : '100%',
              paddingTop: isLg && !isWrapped ? '14.625rem' : 0
            }}
          >
            <div className="flex flex-col gap-y-[0.25rem] lg:gap-y-[0.75rem]">
              <div className="flex flex-col tb:flex-row tb:flex-wrap gap-x-[1rem] gap-y-[0.25rem] lg:gap-y-[0.75rem] flex-grow ">
                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={language === 'arabic' ? "اسمك" : "Your name"}
                    placeholder={language === 'arabic' ? "أدخل اسمك بالكامل" : "Your full name"}
                    value={formData.Referred_by_client_name}
                    onChange={handleReferrerNameChange}
                    invalid={isReferrerNameInvalid}
                    hint={isReferrerNameInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.NAME_REQUIRED : VALIDATION_HINTS.NAME_REQUIRED) : undefined}
                  />
                </div>

                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={language === 'arabic' ? "بريدك الإلكتروني" : "Your email address"}
                    placeholder={language === 'arabic' ? "عنوان بريدك للتواصل" : "Your contact email"}
                    value={formData.Referrer_email}
                    onChange={handleReferrerEmailChange}
                    invalid={isReferrerEmailInvalid || isDuplicateEmail}
                    hint={isReferrerEmailInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.EMAIL_INVALID : VALIDATION_HINTS.EMAIL_INVALID) : isDuplicateEmail ? (language === 'arabic' ? VALIDATION_HINTS_AR.DUPLICATE_EMAIL : VALIDATION_HINTS.DUPLICATE_EMAIL) : undefined}
                  />
                </div>
              </div>

              <div className="flex flex-col tb:flex-row tb:flex-wrap gap-x-[1rem] gap-y-[0.25rem] lg:gap-y-[0.75rem] flex-grow">
                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={language === 'arabic' ? "رقم هاتفك" : "Your phone number"}
                    placeholder={language === 'arabic' ? "رقم هاتفك" : "Your phone number"}
                    value={formData.Referrer_phone}
                    onChange={handleReferrerPhoneChange}
                    invalid={isReferrerPhoneInvalid || isDuplicatePhone}
                    hint={isReferrerPhoneInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.PHONE_INVALID : VALIDATION_HINTS.PHONE_INVALID) : isDuplicatePhone ? (language === 'arabic' ? VALIDATION_HINTS_AR.DUPLICATE_PHONE : VALIDATION_HINTS.DUPLICATE_PHONE) : undefined}
                  />
                </div>

                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={language === 'arabic' ? "اسم المدعو" : "Your invitee's name"}
                    placeholder={language === 'arabic' ? "أدخل اسم الشخص الذي ترغب بدعوته" : "Your invitee's name"}
                    value={formData.Last_Name}
                    onChange={handleInviteeNameChange}
                    invalid={isInviteeNameInvalid}
                    hint={isInviteeNameInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.NAME_REQUIRED : VALIDATION_HINTS.NAME_REQUIRED) : undefined}
                  />
                </div>
              </div>

              <div className="flex flex-col tb:flex-row tb:flex-wrap gap-x-[1rem] gap-y-[0.25rem] lg:gap-y-[0.75rem] flex-grow">
                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={language === 'arabic' ? "البريد الإلكتروني للمدعو" : "Your invitee's email"}
                    placeholder={language === 'arabic' ? "عنوان بريد الشخص الذي ترغب بدعوته للتواصل" : "Your invitee's email"}
                    value={formData.Email}
                    onChange={handleInviteeEmailChange}
                    invalid={isInviteeEmailInvalid || isDuplicateEmail}
                    hint={isInviteeEmailInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.EMAIL_INVALID : VALIDATION_HINTS.EMAIL_INVALID) : isDuplicateEmail ? (language === 'arabic' ? VALIDATION_HINTS_AR.DUPLICATE_EMAIL : VALIDATION_HINTS.DUPLICATE_EMAIL) : undefined}
                  />
                </div>

                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={language === 'arabic' ? "رقم هاتف المدعو" : "Your invitee's phone number"}
                    placeholder={language === 'arabic' ? "رقم هاتف الشخص الذي ترغب بدعوته" : "Your invitee's phone"}
                    value={formData.Phone_number}
                    onChange={handleInviteePhoneChange}
                    invalid={isInviteePhoneInvalid || isDuplicatePhone}
                    hint={isInviteePhoneInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.PHONE_INVALID : VALIDATION_HINTS.PHONE_INVALID) : isDuplicatePhone ? (language === 'arabic' ? VALIDATION_HINTS_AR.DUPLICATE_PHONE : VALIDATION_HINTS.DUPLICATE_PHONE) : undefined}
                  />
                </div>
              </div>

              <div style={{ direction: "ltr" }} className="flex-grow">
                <DropdownField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "الخبير المفضل (اختياري)" : "Recommended expert (optional)"}
                  placeholder={language === 'arabic' ? "اسم الخبير الذي ترغب بالتعامل معه:" : "Your expert choice"}
                  value={formData.Recommended_Expert}
                  // Options come from Strapi's team-member collection, filtered
                  // to Beauty Therapists by the parent page. The name is used
                  // as both label and value so Zoho's Recommended_Expert field
                  // stays human-readable.
                  options={recommendedExperts.map((name) => ({
                    label: name,
                    value: name,
                    icon: <User />,
                  }))}
                  onChange={(value) => setFormData(prev => ({ ...prev, Recommended_Expert: value }))}
                  optional={true}
                />
              </div>

              <div style={{ direction: "ltr" }} className="flex-grow">
                <TextAreaField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={language === 'arabic' ? "ملاحظات شخصية (اختياري)" : "Personal notes (optional)"}
                  placeholder={language === 'arabic' ? "اكتب أي معلومات إضافية ترغبين في مشاركتها معنا" : "Write anything else you'd like to share with us"}
                  value={formData.Personal_Notes}
                  onChange={handleNotesChange}
                  invalid={isNotesInvalid}
                  hint={isNotesInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.CHARACTER_LIMIT_REACHED : VALIDATION_HINTS.CHARACTER_LIMIT_REACHED) : undefined}
                  optional={true}
                />
              </div>
            </div>
            <SubmitFormButton
              isLg={isLg}
              language={language}
              buttonText={
                submitStatus === 'success'
                  ? (language === 'arabic' ? "تم إرسال الإحالة بنجاح" : "Successfully referred")
                  : submitStatus === 'error'
                    ? (language === 'arabic' ? "تعذر إرسال الإحالة" : "Referral not submitted")
                    : (language === 'arabic' ? "ارسلوا إلينا إحالتك" : "Send us your referral")
              }
              wrapperText={
                submitStatus === 'success'
                  ? (language === 'arabic' ? "شكرًا لمشاركتكم ريڤيڤ. سيتواصل فريقنا مع الشخص الذي أحلتموه قريبًا، وستُفعَّل مزاياكم الخاصة بمجرد انضمامه كعضو في ريڤيڤ" : "Thank you for sharing Rivive. Your referral will hear from us soon, and your benefits will unlock once they become a member.")
                  : submitStatus === 'error'
                    ? (language === 'arabic' ? "تعذّر إرسال الإحالة. يُرجى المحاولة مرة أخرى أو التواصل معنا مباشرة." : "Your referral couldn't be submitted. Please try again or contact us directly.")
                    : ''
              }
              privacyText={
                language === 'arabic'
                  ? "بإرسال هذا النموذج، فإنكم توافقون على سياسة الخصوصية الخاصة بنا. نحن نتعامل مع معلوماتكم الشخصية بالعناية نفسها التي نوليها لكل علاج نقدّمه."
                  : "By submitting this form, you agree to our Privacy Policy. We treat your personal information with the same care we bring to every treatment."
              }
              resetButtonText={language === 'arabic' ? "إعادة تعيين النموذج" : "Reset the form"}
              onClick={handleSubmit}
              onReset={() => {
                setFormData({
                  Referred_by_client_name: "",
                  Referrer_email: "",
                  Referrer_phone: "",
                  Last_Name: "",
                  Email: "",
                  Phone_number: "",
                  Recommended_Expert: "",
                  Personal_Notes: ""
                });
                setSubmitStatus('idle');
              }}
              disabled={hasValidationErrors || submitStatus === 'submitting'}
              wrapperBgColor={
                submitStatus === 'success'
                  ? 'bg-greens-green-2'
                  : submitStatus === 'error'
                    ? 'bg-state-error-base'
                    : 'bg-greens-green-2'
              }
            />
          </div>
        </div>
      </div>

    </section >
  );
}


export default ReferralSection;
